import type { HttpLayer } from '../http.js';
import type { DeviceIdentity } from '../device-identity.js';
import type { SDKConfig } from '../http.js';
import type { AgentConfig } from '../types/sessions.js';
import type { InternalAgentVisibility } from '../types/sessions.js';
import type { ShogunChopResource } from '../resources/shogunchop.js';
import type { WebSocketFactory } from './types.js';
import { ChatSession } from './chat-session.js';
import type { HistoryCache } from '../cache/history-cache.js';
import {
  BridgeSessionSource,
  HttpSessionRegistrar,
} from '../resources/bridge-session-source.js';

export interface OpenDraftParams {
  workspaceId?: string;
  personaId?: string;
}

/** Notified with the resource's live handle, or null while none is held. */
export type LiveSessionHandler = (session: ChatSession | null) => void;

interface DraftClaim {
  epoch: number;
}

export interface CreateChatParams {
  personaId?: string;
  personaName?: string;
  agentConfig?: AgentConfig;
  /**
   * Explicit agent process for the session. When omitted, the backend
   * resolves the session persona's default process → seeded Simple and
   * materializes the result. Every session runs under a process —
   * there is no legacy mode path (agent-processes cutover).
   */
  agentProcessId?: string;
  /** Explicit chat override; omitted keeps the persona preference live. */
  automaticProcessSelection?: boolean;
  internalAgentVisibility?: InternalAgentVisibility;
  /**
   * Pin the session's model to every execution in the turn.
   * Omitted ⇒ the backend's ratified default,
   * `false`. The client never fabricates a value — see the omit-if-undefined
   * note in `create` below.
   */
  pinModelToTurn?: boolean;
  deviceId?: string;
  name?: string;
  /**
   * Param-schema fingerprint from `getParamSchema`. Required
   * when `agentConfig` sets completion params or names a provider/model pair
   * — the backend's completion-params write guard 422s without it.
   */
  fingerprint?: string;
}

// NOTE: the former DEFAULT_AGENT_CONFIG (a fabricated groq/openai/gpt-oss-20b
// blob) is DELETED. It was the source
// of a defect: the client made up a provider/model, the backend stored it, and the
// per-turn merge let it permanently shadow the persona. The effective config is
// now resolved SERVER-SIDE from the persona; the client sends
// agent_config ONLY when a caller explicitly provides one.

const DEFAULT_USER_CONFIG = {
  settings: {
    max_history_messages: 50,
    max_history_tokens: 4000,
  },
};

export class ChatResource {
  private http: HttpLayer;
  private config: SDKConfig;
  private shogunchopResource: ShogunChopResource | null;
  private webSocketFactory: WebSocketFactory | undefined;
  /**
   * The shared per-tab web session-event source.
   * Injected by the app layer via {@link setBridgeSessionSource} once it has
   * opened the tab's `BridgeEventStream` (it needs the live JWT identity, which
   * the stateless SDK does not hold). Null on desktop (the daemon `/events`
   * arm is used instead) and until the app wires it on web.
   */
  private bridgeSessionSource: BridgeSessionSource | null = null;
  private live: ChatSession | null = null;
  private liveHandlers = new Set<LiveSessionHandler>();
  private draftPending: { claim: DraftClaim; promise: Promise<ChatSession> } | null = null;
  private bindEpoch = 0;
  private pendingConnects = new Map<string, Set<Promise<ChatSession>>>();
  private retiring = new Map<string, Set<Promise<void>>>();
  private releasing = new Map<string, number>();

  constructor(
    http: HttpLayer,
    config: SDKConfig,
    shogunchopResource: ShogunChopResource | null,
    private historyCache: HistoryCache,
    private identity: DeviceIdentity | null = null,
  ) {
    this.http = http;
    this.config = config;
    this.shogunchopResource = shogunchopResource;
    this.webSocketFactory = config.webSocketFactory;
  }

  /**
   * Attach the createSDK device identity so `chat.create` can name the
   * client. Plain `new LocalAgentSDK` must leave this null — a
   * minted-unregistered id on the create body 400s "Device not found".
   */
  setDeviceIdentity(identity: DeviceIdentity | null): void {
    this.identity = identity;
  }

  createSessionRegistrar(): HttpSessionRegistrar {
    return new HttpSessionRegistrar(this.http);
  }

  /**
   * Install (or clear, with `null`) the shared per-tab web session-event
   * source. Subsequent `create`/`connect` calls wire the ChatSession's web arm
   * to it. Called by the web app's bridge wiring on
   * open and torn down (`null`) on cleanup.
   */
  setBridgeSessionSource(source: BridgeSessionSource | null): void {
    this.bridgeSessionSource = source;
  }

  /**
   * Subscribe to the live handle. Fires with the new handle whenever this
   * resource replaces or drops it, so a host binds by subscription and can
   * never keep sending through a handle that has since been disconnected
   * (sdk ADR-042).
   */
  onLiveSessionChange(handler: LiveSessionHandler): () => void {
    this.liveHandlers.add(handler);
    return () => { this.liveHandlers.delete(handler); };
  }

  /** The handle this resource currently holds, or null. */
  get liveSession(): ChatSession | null {
    return this.live;
  }

  private setLive(next: ChatSession | null): void {
    const outgoing = this.live;
    if (outgoing === next) return;
    this.live = next;
    if (outgoing) this.retire(outgoing);
    for (const handler of this.liveHandlers) handler(next);
  }

  private retire(session: ChatSession): void {
    session.disconnect();
    const id = session.sessionId;
    if (!id) return;
    track(this.retiring, id, session._deregistered());
  }

  /**
   * Release `sessionIds` before they are deleted: drop the live handle when it
   * is one of them and keep any in-flight `connect` for them from binding, then
   * wait until every handle built for those ids has deregistered its session
   * events, so nothing asks the server for a session after its row is gone
   * (sdk ADR-042).
   */
  async _releaseSessions(sessionIds: ReadonlySet<string>): Promise<void> {
    const liveId = this.live?.sessionId;
    if (liveId && sessionIds.has(liveId)) this.setLive(null);
    for (const id of sessionIds) this.releasing.set(id, (this.releasing.get(id) ?? 0) + 1);
    try {
      // Each rejection already reaches its owner (the connect's awaiter, the stream's onError); this only orders the delete (sdk ADR-042).
      await Promise.allSettled(collect(this.pendingConnects, sessionIds));
      await Promise.allSettled(collect(this.retiring, sessionIds));
    } finally {
      for (const id of sessionIds) {
        const count = (this.releasing.get(id) ?? 1) - 1;
        if (count === 0) this.releasing.delete(id);
        else this.releasing.set(id, count);
      }
    }
  }

  /**
   * Return the in-memory New Chat handle. No backend row exists until the first
   * persist action (`send`, `attachWorkspace`, image ensure).
   *
   * Single-flight: concurrent callers join the pending open rather than each
   * minting a draft the others would immediately orphan.
   */
  async openDraft(params: OpenDraftParams = {}): Promise<ChatSession> {
    if (this.live?.isDraft) {
      return this.live;
    }
    const epoch = ++this.bindEpoch;
    if (this.draftPending) {
      this.draftPending.claim.epoch = epoch;
      return this.draftPending.promise;
    }
    const claim: DraftClaim = { epoch };
    const pending = { claim, promise: this.mintDraft(params, claim) };
    this.draftPending = pending;
    try {
      return await pending.promise;
    } finally {
      if (this.draftPending === pending) this.draftPending = null;
    }
  }

  /**
   * The one "the user asked for a new chat" verb: drop whatever is live and
   * return a fresh draft. Callers that only need the side effect can ignore the
   * result — subscribers are notified either way.
   */
  async startNewChat(params: OpenDraftParams = {}): Promise<ChatSession> {
    this.resetDraft();
    return this.openDraft(params);
  }

  /** Drop the in-memory New Chat handle. Does not delete a persisted session. */
  resetDraft(): void {
    this.bindEpoch++;
    this.draftPending = null;
    this.setLive(null);
  }

  private async mintDraft(params: OpenDraftParams, claim: DraftClaim): Promise<ChatSession> {
    const deviceId = (await this.identity?.resolve()) ?? this.config.deviceId;
    const session = new ChatSession(undefined, params.workspaceId, this.http, deviceId, this.historyCache, this.config.historyPageSize, this.config.historyWindowCeiling);
    session._setPersist((s) => this.persistExisting(s));
    if (params.personaId) {
      await session.setPersona(params.personaId);
    } else {
      await session.seedDefaultPersona();
    }
    if (claim.epoch !== this.bindEpoch) {
      this.retire(session);
      return this.successorDraft(claim) ?? session;
    }
    this.setLive(session);
    return session;
  }

  // A mint superseded by a newer draft request hands its caller that draft, never its own dead handle (sdk ADR-042).
  private successorDraft(claim: DraftClaim): ChatSession | Promise<ChatSession> | null {
    if (this.live?.isDraft) return this.live;
    if (this.draftPending && this.draftPending.claim !== claim) return this.draftPending.promise;
    return null;
  }

  /**
   * Create a new chat session and return a connected ChatSession.
   *
   * When `workspaceId` is omitted (or undefined), the session is created
   * workspace-less (account-scoped) via `POST /v1/sessions/sessions`; its
   * `ChatSession.workspaceId` is undefined and the backend persists
   * `workspace_id = null`.
   */
  async create(
    workspaceId?: string,
    params: CreateChatParams = {},
  ): Promise<ChatSession> {
    const deviceId = params.deviceId ?? (await this.identity?.resolve()) ?? this.config.deviceId;
    const bindEpoch = ++this.bindEpoch;
    const id = await this.postCreate(workspaceId, params, deviceId);
    const chatSession = new ChatSession(id, workspaceId, this.http, deviceId, this.historyCache, this.config.historyPageSize, this.config.historyWindowCeiling);
    await this.connectSession(chatSession);
    await chatSession.loadSettings();
    if (bindEpoch !== this.bindEpoch) {
      this.retire(chatSession);
      return chatSession;
    }
    this.setLive(chatSession);
    return chatSession;
  }

  /** Reconnect an existing session id and return a live ChatSession. */
  async connect(sessionId: string, workspaceId?: string): Promise<ChatSession> {
    if (this.live?.sessionId === sessionId) {
      // Healthy-handle fast path only. A stuck one (transport never wired, or
      // registration failed) is not returned dead — it is rebuilt below and
      // the replacement announced via setLive, so connect() itself heals the
      // session instead of requiring a navigate-away-and-back.
      if (this.live.connectionState === 'connected') {
        return this.live;
      }
    }
    const bindEpoch = ++this.bindEpoch;
    const connecting = this.bindExisting(sessionId, workspaceId, bindEpoch);
    track(this.pendingConnects, sessionId, connecting);
    return connecting;
  }

  private async bindExisting(
    sessionId: string,
    workspaceId: string | undefined,
    bindEpoch: number,
  ): Promise<ChatSession> {
    const chatSession = new ChatSession(
      sessionId,
      workspaceId,
      this.http,
      (await this.identity?.resolve()) ?? this.config.deviceId,
      this.historyCache,
      this.config.historyPageSize,
      this.config.historyWindowCeiling,
    );
    try {
      await this.connectSession(chatSession);
      await chatSession.loadSettings();
    } catch (err) {
      this.retire(chatSession);
      throw err;
    }
    if (bindEpoch !== this.bindEpoch || this.releasing.has(sessionId)) {
      this.retire(chatSession);
      return chatSession;
    }
    this.setLive(chatSession);
    return chatSession;
  }

  private async persistExisting(session: ChatSession): Promise<void> {
    const bindEpoch = ++this.bindEpoch;
    const settings = session.settings;
    const deviceId = (await this.identity?.resolve()) ?? this.config.deviceId ?? session.deviceId;
    const id = await this.postCreate(
      session.workspaceId,
      {
        personaId: settings.personaId,
        agentProcessId: settings.processInherited ? undefined : settings.processId,
        automaticProcessSelection: settings.automaticInherited
          ? undefined
          : settings.automaticProcessSelection,
        pinModelToTurn: settings.pinModelToTurn,
        internalAgentVisibility: settings.internalAgentVisibility,
        deviceId,
      },
      deviceId,
    );
    session._acceptPersist(id);
    // One best-effort connect attempt, never retried in place (no-fallbacks):
    // the backend row already exists, so the persist flow must complete — a
    // throw here would leave the persisted handle without setLive/_notifyPersisted,
    // and a retry-send short-circuits on sessionId, i.e. no recovery path.
    // The failure is surfaced on the handle ('error' + error handlers); the
    // next connect(sessionId) heals by replacement (sdk ADR-042). Degrades to
    // a session whose realtime arm is down until that heal.
    try {
      await this.connectSession(session);
      await session.loadSettings();
    } catch (err) {
      session._failConnection(err instanceof Error ? err.message : String(err));
    }
    this.draftPending = null;
    if (bindEpoch === this.bindEpoch) this.setLive(session);
    else this.retire(session);
    session._notifyPersisted();
  }

  private async postCreate(
    workspaceId: string | undefined,
    params: CreateChatParams,
    deviceId: string | undefined,
  ): Promise<string> {
    const personaName = params.personaName ?? 'Assistant';

    const body: Record<string, unknown> = {
      user_config: DEFAULT_USER_CONFIG,
      description: `Chat with ${personaName}`,
      persona_id: params.personaId,
      device_id: deviceId,
    };
    // No fabricated title: a session created without an explicit name stays
    // sentinel-named and auto-nameable (sessions ADR-024). A fabricated
    // placeholder reads as a real title to the once-only namer's flag.
    if (params.name !== undefined) {
      body.name = params.name;
    }
    if (params.agentConfig !== undefined) {
      body.agent_config = params.agentConfig;
    }
    if (params.fingerprint !== undefined) {
      body.fingerprint = params.fingerprint;
    }
    if (params.agentProcessId !== undefined) {
      body.agent_process_id = params.agentProcessId;
    }
    if (params.automaticProcessSelection !== undefined) {
      body.automatic_process_selection = params.automaticProcessSelection;
    }
    if (params.internalAgentVisibility !== undefined) {
      body.internal_agent_visibility = params.internalAgentVisibility;
    }
    if (params.pinModelToTurn !== undefined) {
      body.pin_model_to_turn = params.pinModelToTurn;
    }

    const sessionsPath = workspaceId
      ? `/v1/sessions/${workspaceId}/sessions`
      : `/v1/sessions/sessions`;
    const session = await this.http.post<{ id: string }>(sessionsPath, body);
    return session.id;
  }

  private async connectSession(session: ChatSession): Promise<void> {
    await session._initialize(
      this.bridgeSessionSource,
      this.shogunchopResource,
      this.webSocketFactory,
    );
  }
}

function track<T>(pending: Map<string, Set<Promise<T>>>, id: string, promise: Promise<T>): void {
  const set = pending.get(id) ?? new Set<Promise<T>>();
  set.add(promise);
  pending.set(id, set);
  const settle = (): void => {
    set.delete(promise);
    if (set.size === 0 && pending.get(id) === set) pending.delete(id);
  };
  promise.then(settle, settle);
}

function collect<T>(pending: Map<string, Set<Promise<T>>>, ids: ReadonlySet<string>): Promise<T>[] {
  return [...ids].flatMap((id) => [...(pending.get(id) ?? [])]);
}
